import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

const MENU_EMPTY = {
  id: "",
  menu_name: "",
  menu_icon: "",
  menu_sequence: "",
  is_active: "Y",
  catalog_id: "",
};
const MAP_EMPTY = {
  menu_id: "",
  package_id: "",
  display_order: "",
  is_primary: "N",
};
const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export function BulkMenus() {
  const {
    getBulkMenus,
    insertBulkMenu,
    updateBulkMenu,
    deleteBulkMenu,
    bulkMenuSaving,
    bulkMenusLoading,
    bulkMenuDeletingId,
  } = useBulkCatalogue();
  const [rows, setRows] = useState([]),
    [form, setForm] = useState(MENU_EMPTY),
    [modal, setModal] = useState(false);
  const load = async () => setRows(await getBulkMenus(""));
  useEffect(() => {
    load();
  }, []);
  const edit = (r) => {
    setForm({
      id: String(r?.id ?? ""),
      menu_name: String(r?.menu_name ?? ""),
      menu_icon: String(r?.menu_icon ?? ""),
      menu_sequence: String(r?.menu_sequence ?? ""),
      is_active: String(r?.is_active ?? "Y"),
      catalog_id: String(r?.catalog_id ?? ""),
    });
    setModal(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const ok = form.id
      ? await updateBulkMenu(form)
      : await insertBulkMenu(form);
    if (ok) {
      setModal(false);
      setForm(MENU_EMPTY);
      load();
    }
  };
  return (
    <section className="space-y-3">
      <Header
        title="Bulk Menu"
        loading={bulkMenusLoading}
        onRefresh={load}
        onAdd={() => {
          setForm(MENU_EMPTY);
          setModal(true);
        }}
        add="Add Menu"
      />
      <Rows
        loading={bulkMenusLoading}
        empty="No bulk menus found."
        rows={rows}
        render={(r, i) => (
          <div
            key={r?.id ?? i}
            className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <b className="text-sm text-slate-900">
                {r?.menu_name || "Unnamed Menu"}
              </b>
              <p className="text-[11px] text-slate-500">
                ID #{r?.id || "—"} · Catalog {r?.catalog_id || "—"} · Sequence{" "}
                {r?.menu_sequence || "—"} · {r?.is_active || "—"}
              </p>
            </div>
            <Icon onClick={() => edit(r)}>
              <Edit3 size={13} />
            </Icon>
            <Icon
              danger
              disabled={bulkMenuDeletingId === String(r?.id)}
              onClick={async () => {
                if (await deleteBulkMenu(r?.id)) load();
              }}
            >
              {bulkMenuDeletingId === String(r?.id) ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
            </Icon>
          </div>
        )}
      />
      {modal && (
        <Modal
          title={form.id ? "Edit Bulk Menu" : "Add Bulk Menu"}
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Input
                label="Menu Name"
                name="menu_name"
                value={form.menu_name}
                set={setForm}
              />
              <Input
                label="Menu Icon"
                name="menu_icon"
                value={form.menu_icon}
                set={setForm}
              />
              <Input
                label="Menu Sequence"
                name="menu_sequence"
                value={form.menu_sequence}
                set={setForm}
              />
              <Input
                label="Catalog ID"
                name="catalog_id"
                value={form.catalog_id}
                set={setForm}
              />
              <Select
                label="Is Active"
                name="is_active"
                value={form.is_active}
                set={setForm}
                options={["Y", "N"]}
              />
            </div>
            <Footer
              saving={bulkMenuSaving}
              close={() => setModal(false)}
              edit={!!form.id}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkMenuPackageMappings() {
  const {
    getBulkMenus,
    getBulkMenuPackageMappings,
    insertBulkMenuPackageMapping,
    updateBulkMenuPackageMapping,
    deleteBulkMenuPackageMapping,
    bulkMenuPackageMappingSaving,
    bulkMenuPackageMappingsLoading,
    bulkMenuPackageMappingDeletingKey,
  } = useBulkCatalogue();
  const [menus, setMenus] = useState([]),
    [rows, setRows] = useState([]),
    [form, setForm] = useState(MAP_EMPTY),
    [modal, setModal] = useState(false),
    [editing, setEditing] = useState(false);
  const load = async () => {
    const r = await getBulkMenuPackageMappings("", "");
    setRows(Array.isArray(r) ? r : []);
  };
  const ensureMenus = async () => {
    if (menus.length) return menus;
    const r = await getBulkMenus("");
    const next = Array.isArray(r) ? r : [];
    setMenus(next);
    return next;
  };
  useEffect(() => {
    load();
  }, []);
  const edit = async (r) => {
    await ensureMenus();
    setForm({
      menu_id: String(r?.menu_id ?? ""),
      package_id: String(r?.package_id ?? ""),
      display_order: String(r?.display_order ?? ""),
      is_primary: String(r?.is_primary ?? "N"),
    });
    setEditing(true);
    setModal(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const ok = editing
      ? await updateBulkMenuPackageMapping(form)
      : await insertBulkMenuPackageMapping(form);
    if (ok) {
      setModal(false);
      setEditing(false);
      setForm(MAP_EMPTY);
      load();
    }
  };
  const menuName = (id) =>
    menus.find((x) => String(x?.id) === String(id))?.menu_name ||
    `Menu #${id || "—"}`;
  return (
    <section className="space-y-3">
      <Header
        title="Menu Package Mapping"
        loading={bulkMenuPackageMappingsLoading}
        onRefresh={load}
        onAdd={async () => {
          const m = await ensureMenus();
          setForm({ ...MAP_EMPTY, menu_id: String(m?.[0]?.id ?? "") });
          setEditing(false);
          setModal(true);
        }}
        add="Add Mapping"
      />
      <Rows
        loading={bulkMenuPackageMappingsLoading}
        empty="No menu package mappings found."
        rows={rows}
        render={(r, i) => {
          const key = `${r?.menu_id}:${r?.package_id}`;
          return (
            <div
              key={key || i}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">{menuName(r?.menu_id)}</b>
                <p className="text-[11px] text-slate-500">
                  Package #{r?.package_id || "—"} · Order{" "}
                  {r?.display_order || "—"} · Primary {r?.is_primary || "N"}
                </p>
              </div>
              <Icon onClick={() => edit(r)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={bulkMenuPackageMappingDeletingKey === key}
                onClick={async () => {
                  if (
                    await deleteBulkMenuPackageMapping(
                      r?.menu_id,
                      r?.package_id,
                    )
                  )
                    load();
                }}
              >
                {bulkMenuPackageMappingDeletingKey === key ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />
      {modal && (
        <Modal
          title={
            editing ? "Edit Menu Package Mapping" : "Add Menu Package Mapping"
          }
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <label>
                <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Menu
                </span>
                <select
                  name="menu_id"
                  value={form.menu_id}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, menu_id: e.target.value }))
                  }
                  className={field}
                  disabled={editing}
                >
                  <option value="">Select Menu</option>
                  {menus.map((m) => (
                    <option key={m?.id} value={m?.id}>
                      {m?.menu_name || `Menu #${m?.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <Input
                label="Package ID"
                name="package_id"
                value={form.package_id}
                set={setForm}
                disabled={editing}
              />
              <Input
                label="Display Order"
                name="display_order"
                value={form.display_order}
                set={setForm}
              />
              <Select
                label="Is Primary"
                name="is_primary"
                value={form.is_primary}
                set={setForm}
                options={["Y", "N"]}
              />
            </div>
            <Footer
              saving={bulkMenuPackageMappingSaving}
              close={() => setModal(false)}
              edit={editing}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

function Header({ title, loading, onRefresh, onAdd, add }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-[11px] text-slate-500">
          Bulk catalogue package management
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
        <button
          onClick={onAdd}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
        >
          <Plus size={13} />
          {add}
        </button>
      </div>
    </div>
  );
}
function Rows({ loading, empty, rows, render }) {
  if (loading)
    return <div className="h-24 animate-pulse rounded-xl border bg-white" />;
  if (!rows?.length)
    return (
      <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
        {empty}
      </div>
    );
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {rows.map(render)}
    </div>
  );
}
function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={close}>
            <X size={17} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Input({ label, name, value, set, disabled }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        name={name}
        value={value}
        disabled={disabled}
        onChange={(e) => set((p) => ({ ...p, [name]: e.target.value }))}
        className={field}
      />
    </label>
  );
}
function Select({ label, name, value, set, options }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        name={name}
        value={value}
        onChange={(e) => set((p) => ({ ...p, [name]: e.target.value }))}
        className={field}
      >
        {options.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
      </select>
    </label>
  );
}
function Footer({ saving, close, edit }) {
  return (
    <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={close}
        className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
      >
        Cancel
      </button>
      <button
        disabled={saving}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
      >
        {saving ? (
          <Loader2 size={13} className="animate-spin" />
        ) : edit ? (
          <Edit3 size={13} />
        ) : (
          <Plus size={13} />
        )}{" "}
        {saving ? "Saving..." : edit ? "Update" : "Save"}
      </button>
    </div>
  );
}
function Icon({ children, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border ${danger ? "border-rose-100 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-500"}`}
    >
      {children}
    </button>
  );
}
